// @ts-nocheck
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Layers,
  Search,
  Plus,
  Loader2,
  Sparkles,
  ArrowLeft,
  Building2,
  Calendar,
  FileText,
  Boxes,
  Zap,
  RotateCcw,
  CheckCircle2,
  Scissors,
  Factory,
  ChevronLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { trpcClient } from "@/lib/trpc";
import { toast } from "sonner";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import ProductionOrderForm from "./ProductionOrderForm";
import { OrderPoolWorkbench } from "./components/OrderPoolWorkbench";
import { mergeUniqueRoutingSteps } from "./components/routing-dedup.helper";
import { PrintLayoutForm, type PrintLayoutFormSuccessResult } from "../../tech/layouts/PrintLayoutForm";
import {
  SelectPrepressItemModal,
  type PrepressPoolGroup,
} from "./components/SelectPrepressItemModal";
import {
  type PoolItem,
  calculateGangRunSheets,
  checkHomogeneity,
} from "./components/packaging-combine.engine";
import type { ProductionOrderFormData } from "../../../shared/schemas/production-order.schema";

import { toVnDateString } from "@apv-erp/status";
export default function ProductionOrderCreate() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const searchParams = useSearch({ strict: false }) as {
    salesOrderId?: string;
    /** "id1,id2" (Bể Chờ Lập Lệnh, hộp Lập Lệnh SX) hoặc mảng */
    detailIds?: string | string[];
    /** tên cũ — tab "Đã bình bài - Chờ tạo lệnh" ở /layouts gửi mảng orderDetailIds */
    orderDetailIds?: string | string[];
    /** "Lập Lệnh SX F0" trên bài bình chỉ gửi layoutId → nạp các dòng của bài */
    layoutId?: string;
    customerId?: string;
    layoutCode?: string;
    poType?: string;
    mode?: string;
  };

  // Chuẩn hoá mọi lối vào form: detailIds (chuỗi / mảng), orderDetailIds (cũ), layoutId (đọc dòng từ bài bình)
  const urlDetailIds = useMemo(() => {
    const toList = (v: unknown): string[] =>
      Array.isArray(v)
        ? v.flatMap((x) => String(x).split(","))
        : typeof v === "string"
          ? v.split(",")
          : [];
    return Array.from(
      new Set(
        [...toList(searchParams.detailIds), ...toList(searchParams.orderDetailIds)]
          .map((s) => s.trim())
          .filter(Boolean),
      ),
    );
  }, [searchParams.detailIds, searchParams.orderDetailIds]);

  const { data: layoutForPrefill } = useQuery({
    queryKey: ["layout-for-po-create", searchParams.layoutId],
    queryFn: async () => trpcClient.prepress.getPrintLayout.query({ id: searchParams.layoutId as string }),
    enabled: !!searchParams.layoutId && urlDetailIds.length === 0,
  });
  const layoutDetailIds = useMemo(
    () =>
      ((layoutForPrefill as any)?.orderDetails || [])
        .filter((d: any) => !d.isDeleted && d.status !== "CANCELLED")
        .map((d: any) => d.id as string),
    [layoutForPrefill],
  );
  const effectiveLayoutCode =
    searchParams.layoutCode || (layoutForPrefill as any)?.layoutCode || undefined;

  const hasInitialParams =
    !!searchParams.salesOrderId ||
    urlDetailIds.length > 0 ||
    !!searchParams.layoutId ||
    !!searchParams.layoutCode ||
    searchParams.mode === "form" ||
    searchParams.mode === "manual";

  // View state: "WORKBENCH" (chọn mặt hàng từ bể chờ) → "PREPRESS" (bình file ngay tại đây) → "FORM" (rà soát và lưu lệnh)
  const [currentView, setCurrentView] = useState<"WORKBENCH" | "PREPRESS" | "FORM">(
    hasInitialParams ? "FORM" : "WORKBENCH"
  );
  // Bình file trong luồng tạo lệnh: cùng một người ghép bài rồi lập lệnh, không phải sang màn Bình file riêng
  const [prepressTarget, setPrepressTarget] = useState<{ detailIds: string[]; layoutId: string | null } | null>(null);
  const { data: prepressDetailsData, isLoading: isLoadingPrepressDetails } = useQuery({
    queryKey: ["po-create-prepress-details", prepressTarget?.detailIds.join(",")],
    queryFn: () =>
      trpcClient.prepress.listPendingOrderDetails.query({
        ids: prepressTarget!.detailIds,
        includeExisting: true,
        limit: 100,
      }),
    enabled: currentView === "PREPRESS" && !!prepressTarget && !prepressTarget.layoutId && prepressTarget.detailIds.length > 0,
  });
  const prepressInitialDetails = useMemo(() => (prepressDetailsData as any)?.items || [], [prepressDetailsData]);

  // Vừa "Lưu & lập lệnh": chờ bài bình nạp xong vào form rồi mới hiện form (thao tác trước đó sẽ mất khi form dựng lại)
  const [pendingLayoutPrefill, setPendingLayoutPrefill] = useState<string | null>(null);
  // Bài bình đã nạp vào form (khoá theo số liệu) — tránh dựng lại form khi chỉ tải lại dữ liệu y hệt
  const appliedLayoutKeyRef = useRef<string | null>(null);

  const openPrepress = useCallback((detailIds: string[], layoutId: string | null = null) => {
    if (!layoutId && detailIds.length === 0) return;
    setPrepressTarget({ detailIds, layoutId });
    setCurrentView("PREPRESS");
  }, []);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [prepressModalOpen, setPrepressModalOpen] = useState(false);

  // Selected items from Workbench
  const [activeItems, setActiveItems] = useState<PoolItem[]>([]);
  const [activeDispatchMode, setActiveDispatchMode] = useState<
    "SINGLE" | "GANG_RUN" | "SPLIT"
  >("SINGLE");

  const [formKey, setFormKey] = useState(1);
  const [formDefaults, setFormDefaults] = useState<
    Partial<ProductionOrderFormData>
  >({
    priority: "NORMAL",
    status: "DRAFT",
    quantity: 1,
    qtyPlan: 1,
    qtyAllowance: 0,
    deadline: toVnDateString(),
  });

  // Query order if passed via searchParams (e.g. from OrderView)
  const { data: orderData } = useQuery({
    queryKey: ["order-for-po-create", searchParams.salesOrderId],
    queryFn: async () => {
      if (!searchParams.salesOrderId) return null;
      return await trpcClient.orders.getById.query({
        id: searchParams.salesOrderId,
      });
    },
    enabled: !!searchParams.salesOrderId,
  });

  // Query multi-order details when detailIds or layoutCode are passed (e.g. from Gang-Run PrintLayoutView across multiple orders)
  const detailIdArray = urlDetailIds.length > 0 ? urlDetailIds : layoutDetailIds;
  const { data: multiOrderDetailsData } = useQuery({
    queryKey: ["multi-details-for-po-create", detailIdArray.join(","), searchParams.layoutCode],
    queryFn: async () => {
      if (detailIdArray.length > 0) {
        return await trpcClient.prepress.listPendingOrderDetails.query({
          ids: detailIdArray,
          includeExisting: true,
        });
      }
      if (searchParams.layoutCode) {
        return await trpcClient.prepress.listPendingOrderDetails.query({
          search: searchParams.layoutCode,
          includeExisting: true,
        });
      }
      return null;
    },
    enabled: detailIdArray.length > 0 || Boolean(searchParams.layoutCode),
  });

  const handlePrepressSaved = async (result: PrintLayoutFormSuccessResult) => {
    void queryClient.invalidateQueries({ queryKey: ["production-orders-pending-items-pool"] });
    if (result.createPo && result.id) {
      // Bài bình vừa lưu → nạp thẳng vào form lệnh (cùng lối ?layoutId= như từ màn Bình file).
      // Sang bước Lệnh trước rồi mới tải lại bài bình, để form nhận số liệu bài vừa sửa.
      setPrepressTarget(null);
      setPendingLayoutPrefill(result.id);
      appliedLayoutKeyRef.current = null;
      setCurrentView("FORM");
      navigate({ to: "/production-orders/create", search: { layoutId: result.id } as any, replace: true });
      void queryClient.invalidateQueries({ queryKey: ["layout-for-po-create", result.id] });
      return;
    }
    // Chỉ lưu bài bình: về bể chờ, các dòng đã có khổ in + số con để lập lệnh
    setPrepressTarget(null);
    setCurrentView("WORKBENCH");
  };

  // Dispatch items selected from OrderPoolWorkbench
  const handleSelectItemsForPO = useCallback(
    (
      items: PoolItem[],
      mode: "SINGLE" | "GANG_RUN" | "SPLIT",
      splitQty?: number
    ) => {
      if (!items || items.length === 0) return;

      setActiveItems(items);
      setActiveDispatchMode(mode);

      const isGang = items.length > 1;
      const primaryItem = items[0];

      // Calculate total plan quantity
      let totalQty = 0;
      if (mode === "SPLIT" && splitQty && splitQty > 0) {
        totalQty = splitQty;
      } else {
        totalQty = items.reduce(
          (sum, i) => sum + (i.remainingPlanQty || i.quantity || 1),
          0
        );
      }

      // Calculate Gang Run Sheets (Tiered Waste Matrix + Area Weights, respecting inherited prepressPrintSheets)
      const maxNetSheets = Math.max(
        0,
        ...items.map((i) => {
          const q =
            mode === "SPLIT" && splitQty && splitQty > 0
              ? splitQty
              : i.remainingPlanQty || i.quantity || 1;
          return Math.ceil(q / Math.max(1, i.effectiveUps || 1));
        })
      );
      const maxPrepressSheets = Math.max(
        0,
        ...items.map((i) => Number((i as any).prepressPrintSheets) || 0)
      );
      const inheritedAllowance =
        mode !== "SPLIT" && maxPrepressSheets >= maxNetSheets && maxPrepressSheets > 0
          ? maxPrepressSheets - maxNetSheets
          : undefined;

      const gangStats = calculateGangRunSheets(
        items.map((i) => ({
          id: i.id,
          quantity:
            mode === "SPLIT" && splitQty && splitQty > 0
              ? splitQty
              : i.remainingPlanQty || i.quantity || 1,
          ups: i.effectiveUps || 1,
          lengthMm: i.flatLength || i.length || 100,
          widthMm: i.flatWidth || i.width || 100,
        })),
        inheritedAllowance
      );

      // Prepress codes / Layout codes
      const prepressCodes = items
        .map((i) => i.prepressCode)
        .filter(Boolean);
      const uniquePrepressCode =
        prepressCodes.length > 0 ? prepressCodes[0] : null;

      // Paper sizes
      const paperSizes = items
        .map((i) => i.effectivePaperSize)
        .filter(Boolean);
      const effectivePaperSize = paperSizes[0] || "";

      // Total UPS on sheet
      const totalUpsOnSheet = items.reduce(
        (sum, i) => sum + (i.effectiveUps || 1),
        0
      );

      // Product Name label
      let poProductName = primaryItem.productName;
      if (isGang) {
        poProductName = `[BÀI GHÉP ${items.length} SP] ${items
          .map((i) => i.productName)
          .join(" + ")}`;
      } else if (mode === "SPLIT") {
        poProductName = `[TÁCH ĐỢT] ${primaryItem.productName} (${(
          splitQty || primaryItem.remainingPlanQty
        ).toLocaleString()} cái)`;
      }

      // Order Sources for Canonical Form (Tab 4 & DB relation)
      const orderSources = items.map((i) => {
        const itemPlanQty =
          mode === "SPLIT" && splitQty && splitQty > 0
            ? splitQty
            : i.remainingPlanQty || i.quantity;

        return {
          orderDetailId: i.id,
          salesOrderId: i.orderId,
          productId: i.productId || "",
          productCode: i.productCode || "",
          productName: i.productName,
          quantity: i.quantity,
          plannedQty: itemPlanQty,
          unit: i.unit || "cái",
          pcsPerSheet: i.effectiveUps || 1,
          ups: i.effectiveUps || 1,
          materialId: i.materialId || null,
          productProcesses: i.productProcesses || [],
          productMaterials: [],
        };
      });

      const effectiveRunSheets =
        mode !== "SPLIT" && maxPrepressSheets > 0
          ? maxPrepressSheets
          : gangStats.totalRunSheetsNeeded;
      const effectiveWasteAllowance =
        mode !== "SPLIT" && maxPrepressSheets > 0
          ? Math.max(0, maxPrepressSheets - maxNetSheets)
          : gangStats.allowanceSheets;

      const newDefaults: Partial<ProductionOrderFormData> & { printLayoutId?: string | null } = {
        customerId: primaryItem.customerId,
        customerName: primaryItem.customerName,
        salesOrderId: primaryItem.orderId,
        salesOrderNumber: primaryItem.orderCode,
        productId: primaryItem.productId || "",
        productName: poProductName,
        orderDetailId: primaryItem.id,
        printLayoutId: (primaryItem as any).printLayoutId || null,
        quantity: totalQty,
        qtyPlan: totalQty,
        qtyAllowance: 0,
        layoutCode: uniquePrepressCode || primaryItem.layoutCode || "",
        printingPaperSize: effectivePaperSize,
        prepressPcsPerSheet: totalUpsOnSheet,
        prepressWasteAllowance: effectiveWasteAllowance,
        totalPlanSheets: effectiveRunSheets,
        totalMaterialRequired: effectiveRunSheets,
        runSheetsNeeded: effectiveRunSheets,
        deadline: primaryItem.deliveryDeadline
          ? toVnDateString(primaryItem.deliveryDeadline)
          : toVnDateString(),
        priority: "NORMAL",
        status: "DRAFT",
        isComposite: isGang,
        poType: isGang ? "GANG_RUN" : mode === "SPLIT" ? "SPLIT_BATCH" : "SINGLE",
        poReference: uniquePrepressCode
          ? `PO-${uniquePrepressCode}`
          : `PO-${primaryItem.orderCode}`,
        orderSources,
        toolings: [],
        // Lệnh ghép: quy trình khởi tạo = hợp công đoạn của mọi sản phẩm trong bài (sản phẩm khác quy trình vẫn ghép được);
        // người lập lệnh chỉnh lại ở tab Kế hoạch sản xuất. Dựng sẵn ở đây để các effect của form không ghi đè lẫn nhau.
        ...(isGang
          ? {
              components: [
                {
                  isMain: true,
                  componentName: "Main",
                  routingSteps: mergeUniqueRoutingSteps(
                    orderSources[0]?.productProcesses || [],
                    orderSources.slice(1).map((src) => ({
                      processes: src.productProcesses || [],
                      productCode: src.productCode || src.productName,
                      productName: src.productName,
                      quantity: Number(src.plannedQty ?? src.quantity) || 0,
                      orderDetailId: src.orderDetailId,
                    })),
                    {
                      runSheetsNeeded: effectiveRunSheets,
                      primaryProductName: orderSources[0]?.productName,
                      primaryProductCode: orderSources[0]?.productCode,
                      primaryQuantity: Number(orderSources[0]?.plannedQty ?? orderSources[0]?.quantity) || 0,
                      isMultiProduct: true,
                    },
                  ),
                },
              ],
            }
          : {}),
      } as any;

      setFormDefaults(newDefaults);
      setFormKey((prev) => prev + 1);
      setCurrentView("FORM");

      toast.success(
        `Đã nạp ${items.length} mặt hàng vào Lệnh Sản Xuất (${effectivePaperSize || "Khổ chuẩn"}, ${totalUpsOnSheet} UPS, ${gangStats.totalRunSheetsNeeded.toLocaleString()} tờ in).`
      );
    },
    []
  );

  // Auto-populate when layoutForPrefill, orderData, or multiOrderDetailsData loads from URL search params
  useEffect(() => {
    // Đang ở bước Bình file: dữ liệu bài bình tải lại không được kéo người dùng về form lệnh
    if (currentView === "PREPRESS") return;
    // 1. Direct inheritance from PrintLayout (?layoutId=...) — preserves exact Gang-Run & Split-Run quantities!
    if (searchParams.layoutId && layoutForPrefill && urlDetailIds.length === 0) {
      const pl = layoutForPrefill as any;
      const plItems: any[] = Array.isArray(pl.layoutItems) ? pl.layoutItems : [];
      // Chỉ nạp lại form khi bài bình thật sự đổi (bài khác / vừa sửa) — bài bình tải lại y hệt không được dựng lại
      // form lệnh, nếu không quy trình, bù hao… người lập lệnh vừa chỉnh sẽ mất.
      // Khoá theo số liệu ảnh hưởng tới lệnh (không theo updatedAt — có thể đổi mỗi lần tải)
      const layoutKey = `${pl.id}|${pl.sheetSize || ""}|${pl.sheetsPerUp || pl.totalPrintSheets || ""}|${plItems.map((li: any) => `${li.orderDetailId}:${li.ups}:${li.plannedQty}:${li.sheetsPerUp || ""}`).join(",")}`;
      if (plItems.length === 0) setPendingLayoutPrefill(null);
      if (appliedLayoutKeyRef.current === layoutKey) return;
      if (plItems.length > 0) {
        appliedLayoutKeyRef.current = layoutKey;
        const mappedFromLayout: PoolItem[] = plItems
          .filter((li: any) => li?.orderDetail && !li.orderDetail.isDeleted)
          .map((li: any) => {
            const d = li.orderDetail;
            const effectivePaperSize =
              pl.sheetSize || d.prepressPaperSize || d.product?.printFormat || null;
            const effectiveUps = Math.max(1, Number(li.ups || pl.ups || d.prepressUps || 1));
            const effectiveSheets = Number(
              li.sheetsPerUp || pl.sheetsPerUp || pl.totalPrintSheets || d.prepressPrintSheets || 0,
            );
            const itemTargetQty = Math.max(
              1,
              Number(li.plannedQty || d.remainingPlanQty || d.quantity || 1),
            );
            const hasPrintFormat = Boolean(effectivePaperSize && effectivePaperSize.trim() !== "");

            return {
              id: d.id,
              detailId: d.detailId || d.id,
              orderId: d.orderId || d.order?.id || searchParams.salesOrderId || "",
              orderCode: d.order?.orderId || d.orderCode || "",
              customerId: d.order?.customerId || d.customerId || searchParams.customerId || "",
              customerName: d.order?.customer?.name || d.customerName || "Khách hàng",
              customerCode: d.order?.customer?.code || "",
              productId: d.productId,
              productCode: d.productCode || d.product?.productCode || "",
              productName: d.productName || d.product?.productName || "",
              productImage: d.product?.image || null,
              unit: d.unit || "cái",
              quantity: d.quantity,
              plannedQty: d.plannedQty || 0,
              remainingPlanQty: itemTargetQty,
              deliveryDeadline: d.deliveryDeadline || d.order?.deliveryDeadline || null,
              prepressCode: pl.layoutCode || d.prepressCode || null,
              layoutCode: pl.layoutCode || d.prepressCode || null,
              printLayoutId: pl.id,
              prepressPrintSheets: effectiveSheets || null,
              effectivePaperSize,
              effectiveUps,
              isValidForProduction: hasPrintFormat,
              missingReason: !hasPrintFormat ? "Thiếu khổ in" : null,
              materialId: d.materialId || d.product?.materialId || null,
              productProcesses: d.product?.productProcesses || [],
            };
          });

        if (mappedFromLayout.length > 0) {
          handleSelectItemsForPO(
            mappedFromLayout,
            mappedFromLayout.length > 1 ? "GANG_RUN" : "SINGLE",
          );
          setPendingLayoutPrefill(null);
          return;
        }
      }
    }

    const rawMultiItems = Array.isArray(multiOrderDetailsData)
      ? multiOrderDetailsData
      : (multiOrderDetailsData as any)?.items || [];

    if ((detailIdArray.length > 0 || searchParams.layoutCode) && rawMultiItems.length > 0) {
      const mappedItems: PoolItem[] = rawMultiItems.map((d: any) => {
        const effectivePaperSize =
          d.prepressPaperSize || d.printLayout?.sheetSize || d.product?.printFormat || null;
        const effectiveUps =
          d.prepressUps || d.printLayout?.ups || d.product?.outs || 1;
        const hasPrintFormat = Boolean(effectivePaperSize && effectivePaperSize.trim() !== "");
        const hasUps = Boolean(effectiveUps && effectiveUps >= 1);

        return {
          id: d.id,
          detailId: d.detailId || d.id,
          orderId: d.orderId || d.order?.id || searchParams.salesOrderId || "",
          orderCode: d.order?.orderId || d.orderCode || "",
          customerId: d.order?.customerId || d.customerId || searchParams.customerId || "",
          customerName: d.order?.customer?.name || d.customerName || "Khách hàng",
          customerCode: d.order?.customer?.code || "",
          productId: d.productId,
          productCode: d.productCode || d.product?.productCode || "",
          productName: d.productName || d.product?.productName || "",
          productImage: d.product?.image || null,
          unit: d.unit || "cái",
          quantity: d.quantity,
          plannedQty: d.plannedQty || 0,
          remainingPlanQty:
            d.remainingPlanQty !== undefined && d.remainingPlanQty !== null && d.remainingPlanQty > 0
              ? d.remainingPlanQty
              : d.quantity || 0,
          deliveryDeadline: d.deliveryDeadline || d.order?.deliveryDeadline || null,
          prepressCode: effectiveLayoutCode || d.prepressCode || d.printLayout?.layoutCode || null,
          layoutCode: effectiveLayoutCode || d.prepressCode || d.printLayout?.layoutCode || null,
          printLayoutId: d.printLayoutId || searchParams.layoutId || null,
          prepressPrintSheets: d.prepressPrintSheets || null,
          effectivePaperSize,
          effectiveUps,
          isValidForProduction: hasPrintFormat && hasUps,
          missingReason: !hasPrintFormat || !hasUps ? "Thiếu khổ in hoặc số UP" : null,
          materialId: d.materialId || d.product?.materialId || null,
          productProcesses: d.product?.productProcesses || [],
        };
      });

      handleSelectItemsForPO(
        mappedItems,
        mappedItems.length > 1 || searchParams.poType === "GANG_RUN" ? "GANG_RUN" : "SINGLE"
      );
      return;
    }

    if (!orderData) return;
    const allDetails = (orderData as any).orderDetails || [];
    const filterIds = urlDetailIds;
    const targetDetails =
      filterIds.length > 0
        ? allDetails.filter((d: any) => filterIds.includes(d.id))
        : allDetails.filter((d: any) => !d.isDeleted && d.status !== "CANCELLED");

    if (targetDetails.length === 0) return;

    const mappedItems: PoolItem[] = targetDetails.map((d: any) => {
      const effectivePaperSize =
        d.prepressPaperSize || d.printLayout?.sheetSize || d.product?.printFormat || null;
      const effectiveUps =
        d.prepressUps || d.printLayout?.ups || d.product?.outs || null;
      const hasPrintFormat = Boolean(effectivePaperSize && effectivePaperSize.trim() !== "");
      const hasUps = Boolean(effectiveUps && effectiveUps >= 1);

      return {
        id: d.id,
        detailId: d.detailId || d.id,
        orderId: orderData.id,
        orderCode: orderData.orderId,
        customerId: orderData.customerId,
        customerName: (orderData as any).customer?.name || "Khách hàng",
        customerCode: (orderData as any).customer?.code || "",
        productId: d.productId,
        productCode: d.productCode || d.product?.productCode || "",
        productName: d.productName || d.product?.productName || "",
        productImage: d.product?.image || null,
        unit: d.unit || "cái",
        quantity: d.quantity,
        plannedQty: d.plannedQty || 0,
        remainingPlanQty:
          d.remainingPlanQty !== undefined && d.remainingPlanQty !== null && d.remainingPlanQty > 0
            ? d.remainingPlanQty
            : d.quantity || 0,
        deliveryDeadline: d.deliveryDeadline || orderData.deliveryDeadline || null,
        prepressCode: effectiveLayoutCode || d.prepressCode || d.printLayout?.layoutCode || null,
        layoutCode: effectiveLayoutCode || d.prepressCode || d.printLayout?.layoutCode || null,
        printLayoutId: d.printLayoutId || searchParams.layoutId || null,
        prepressPrintSheets: d.prepressPrintSheets || null,
        effectivePaperSize,
        effectiveUps,
        isValidForProduction: hasPrintFormat && hasUps,
        missingReason: !hasPrintFormat || !hasUps ? "Thiếu khổ in hoặc số UP" : null,
        materialId: d.materialId || d.product?.materialId || null,
        productProcesses: d.product?.productProcesses || [],
      };
    });

    handleSelectItemsForPO(
      mappedItems,
      mappedItems.length > 1 || searchParams.poType === "GANG_RUN" ? "GANG_RUN" : "SINGLE"
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps -- currentView chỉ để chặn khi đang bình file; quay lại bể chờ không được nạp lại lệnh từ URL
  }, [layoutForPrefill, orderData, multiOrderDetailsData, urlDetailIds, detailIdArray.join(","), effectiveLayoutCode, searchParams.layoutCode, searchParams.layoutId, searchParams.poType, searchParams.salesOrderId, searchParams.customerId, handleSelectItemsForPO]);

  // Handler when user selects a prepress batch from legacy modal
  const handleSelectLegacyPrepressBatch = useCallback(
    (group: PrepressPoolGroup) => {
      const mappedItems: PoolItem[] = group.items.map((i) => ({
        id: i.id,
        detailId: i.detailId,
        orderId: i.orderId,
        orderCode: i.orderCode,
        customerId: i.customerId,
        customerName: i.customerName,
        productId: i.productId,
        productCode: i.productCode,
        productName: i.productName,
        unit: i.unit,
        quantity: i.quantity,
        plannedQty: i.plannedQty,
        remainingPlanQty: i.remainingPlanQty,
        prepressCode: group.prepressCode,
        layoutCode: group.prepressCode,
        effectivePaperSize: i.prepressPaperSize || group.paperSize || null,
        effectiveUps: i.prepressUps || 1,
        isValidForProduction: true,
        deliveryDeadline: i.deliveryDeadline,
      }));

      handleSelectItemsForPO(
        mappedItems,
        mappedItems.length > 1 ? "GANG_RUN" : "SINGLE"
      );
      setPrepressModalOpen(false);
    },
    [handleSelectItemsForPO]
  );

  // Form Submit handler
  const handleSubmit = async (data: ProductionOrderFormData) => {
    setIsSubmitting(true);
    try {
      const inheritedPrintLayoutId =
        (data as any).printLayoutId ||
        (formDefaults as any).printLayoutId ||
        searchParams.layoutId ||
        undefined;
      const res = await trpcClient.productionOrders.create.mutate({
        ...(data as any),
        ...(inheritedPrintLayoutId ? { printLayoutId: inheritedPrintLayoutId } : {}),
      });
      const targetId = res?.orderId || res?.id;

      if (data.printLayouts && data.printLayouts.length > 0 && res?.id) {
        try {
          await trpcClient.productionOrders.savePrintLayouts.mutate({
            productionOrderId: res.id,
            layouts: data.printLayouts,
          });
        } catch (e) {
          console.warn("Could not save print layouts:", e);
        }
      }

      if (data.materialPlans && data.materialPlans.length > 0 && res?.id) {
        try {
          await trpcClient.productionOrders.saveMaterialPlans.mutate({
            productionOrderId: res.id,
            plans: data.materialPlans,
          });
        } catch (e) {
          console.warn("Could not save material plans:", e);
        }
      }

      toast.success("Khởi tạo Lệnh Sản Xuất thành công!");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["production-orders"] }),
        queryClient.invalidateQueries({ queryKey: ["print-layouts-workbench"] }),
        queryClient.invalidateQueries({ queryKey: ["prepress-hub-counts"] }),
        queryClient.invalidateQueries({ queryKey: ["prepress-hub-list"] }),
      ]);
      navigate({
        to: "/production-orders/$orderId",
        params: { orderId: targetId },
        replace: true,
      });
    } catch (error: any) {
      toast.error(error?.message || "Khởi tạo Lệnh SX thất bại");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <StandardDetailLayout
      title="Khởi tạo Lệnh Sản Xuất"
      breadcrumbs={[
        { label: "Lệnh sản xuất", to: "/production-orders" },
        { label: currentView === "WORKBENCH" ? "Bể đơn hàng chờ lệnh" : currentView === "PREPRESS" ? "Bình file" : "Nhập thông số lệnh" },
      ]}
      onBack={() => {
        if (currentView === "PREPRESS" || (currentView === "FORM" && !hasInitialParams)) {
          setCurrentView("WORKBENCH");
        } else {
          navigate({ to: "/production-orders" });
        }
      }}
      showContextPanel={false}
      actions={
        <div className="flex items-center gap-2 shrink-0">
          {currentView === "FORM" ? (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setCurrentView("WORKBENCH")}
                className="h-8 text-xs text-indigo-600 border-indigo-200 hover:bg-indigo-50 font-medium shadow-2xs"
              >
                <ChevronLeft className="mr-1 h-3.5 w-3.5" /> Đổi mặt hàng khác
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => navigate({ to: "/production-orders" })}
                className="h-8 text-xs min-w-[60px]"
              >
                Hủy
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={isSubmitting}
                className="h-8 text-xs min-w-[100px] bg-indigo-600 hover:bg-indigo-700 text-white font-medium shadow-xs"
                onClick={() => {
                  const submitBtn = document.querySelector(
                    'form button[type="submit"]'
                  ) as HTMLButtonElement;
                  if (submitBtn) submitBtn.click();
                }}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> Đang tạo...
                  </>
                ) : (
                  <>
                    <Plus className="mr-1 h-3.5 w-3.5" /> Tạo Lệnh SX
                  </>
                )}
              </Button>
            </>
          ) : currentView === "PREPRESS" ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setPrepressTarget(null);
                setCurrentView("WORKBENCH");
              }}
              className="h-8 text-xs text-indigo-600 border-indigo-200 hover:bg-indigo-50 font-medium"
            >
              <ChevronLeft className="mr-1 h-3.5 w-3.5" /> Về bể chờ lệnh
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setCurrentView("FORM")}
                className="h-8 text-xs text-indigo-700 border-indigo-200 bg-indigo-50/60 hover:bg-indigo-100 font-medium"
              >
                <Plus className="mr-1 h-3.5 w-3.5" /> Nhập form thủ công
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => navigate({ to: "/production-orders" })}
                className="h-8 text-xs"
              >
                Quay lại danh sách
              </Button>
            </div>
          )}
        </div>
      }
    >
      <div className="w-full space-y-4 px-0 py-3 animate-in fade-in duration-300">
        <PoCreateStepper current={currentView} />
        {/* TẦNG 1: WORKBENCH CHỌN MẶT HÀNG (GATE 1 ENFORCEMENT) */}
        {currentView === "WORKBENCH" ? (
          <OrderPoolWorkbench
            onSelectItemsForPO={handleSelectItemsForPO}
            onOpenLegacyModal={() => setPrepressModalOpen(true)}
            onManualEntry={() => setCurrentView("FORM")}
            onPrepress={(items) => {
              // Cùng một bài bình đã có → mở để sửa / ghép lại; còn lại tạo bài bình mới cho các dòng đã chọn
              const layoutIds = Array.from(new Set(items.map((i: any) => i.printLayoutId).filter(Boolean)));
              const reuse = layoutIds.length === 1 && items.every((i: any) => i.printLayoutId === layoutIds[0]) ? String(layoutIds[0]) : null;
              openPrepress(items.map((i) => i.id), reuse);
            }}
          />
        ) : currentView === "FORM" && pendingLayoutPrefill ? (
          <div className="flex items-center justify-center py-16 text-slate-500 text-sm gap-2" data-testid="po-form-loading-layout">
            <Loader2 className="h-4 w-4 animate-spin" /> Đang nạp bài bình vào lệnh sản xuất...
          </div>
        ) : currentView === "PREPRESS" && prepressTarget ? (
          <div className="rounded-lg border border-emerald-200 bg-white p-3 sm:p-4" data-testid="po-create-prepress-step">
            {!prepressTarget.layoutId && isLoadingPrepressDetails ? (
              <div className="flex items-center justify-center py-16 text-slate-500 text-sm gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Đang nạp mặt hàng để bình file...
              </div>
            ) : (
              <PrintLayoutForm
                key={prepressTarget.layoutId || prepressTarget.detailIds.join(",")}
                mode={prepressTarget.layoutId ? "edit" : "create"}
                layoutId={prepressTarget.layoutId}
                initialDetails={prepressTarget.layoutId ? [] : prepressInitialDetails}
                navigateOnCreatePo={false}
                createPoLabel="Lưu & lập lệnh"
                onSuccess={handlePrepressSaved}
                onCancel={() => {
                  setPrepressTarget(null);
                  setCurrentView(activeItems.length > 0 ? "FORM" : "WORKBENCH");
                }}
              />
            )}
          </div>
        ) : (
          /* TẦNG 2: CANONICAL PRODUCTION ORDER FORM (RULE 2.1 & RULE 8) */
          <div className="space-y-4">
            {/* Banner tóm tắt mặt hàng đang lên lệnh */}
            <div className="bg-gradient-to-r from-indigo-50/90 via-blue-50/80 to-slate-50 border border-indigo-200/80 rounded-lg p-3 sm:p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 shadow-xs">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-10 w-10 rounded-lg bg-indigo-600/10 border border-indigo-200 flex items-center justify-center shrink-0 text-indigo-700">
                  {activeDispatchMode === "GANG_RUN" ? (
                    <Factory className="h-5 w-5" />
                  ) : activeDispatchMode === "SPLIT" ? (
                    <Scissors className="h-5 w-5" />
                  ) : (
                    <FileText className="h-5 w-5" />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge
                      variant="outline"
                      className="bg-indigo-600 text-white border-indigo-600 text-xs font-bold"
                    >
                      {activeDispatchMode === "GANG_RUN"
                        ? `LỆNH GHÉP (${activeItems.length} SP)`
                        : activeDispatchMode === "SPLIT"
                        ? "LỆNH TÁCH ĐỢT"
                        : "LỆNH ĐƠN CHUẨN"}
                    </Badge>
                    <span className="text-xs text-slate-500 font-medium">
                      Đơn:{" "}
                      <strong className="text-slate-800 font-mono">
                        {formDefaults.salesOrderNumber || "PO-REF"}
                      </strong>
                    </span>
                    <span className="text-xs text-slate-500 font-medium">
                      KH:{" "}
                      <strong className="text-slate-800">
                        {formDefaults.customerName || "Khách hàng"}
                      </strong>
                    </span>
                  </div>
                  <div className="text-xs text-slate-600 mt-1 flex items-center gap-3 flex-wrap">
                    <span>
                      Khổ in:{" "}
                      <strong className="font-mono text-slate-800">
                        {formDefaults.printingPaperSize || "Chuẩn"}
                      </strong>
                    </span>
                    <span>•</span>
                    <span>
                      Số con:{" "}
                      <strong className="font-mono text-indigo-600">
                        {formDefaults.prepressPcsPerSheet || 1} UPS
                      </strong>
                    </span>
                    <span>•</span>
                    <span>
                      Dự kiến tờ in:{" "}
                      <strong className="font-mono text-slate-800">
                        {(formDefaults.runSheetsNeeded || 0).toLocaleString()}
                      </strong>{" "}
                      tờ
                    </span>
                    <span>•</span>
                    <span>
                      Tổng SL:{" "}
                      <strong className="font-mono text-indigo-700 font-bold">
                        {(formDefaults.quantity || 0).toLocaleString()}
                      </strong>{" "}
                      cái
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {activeItems.length > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="po-form-open-prepress"
                    onClick={() => {
                      const layoutId = (formDefaults as any).printLayoutId || (activeItems[0] as any)?.printLayoutId || null;
                      openPrepress(activeItems.map((i) => i.id), layoutId);
                    }}
                    className="h-8 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50 shadow-2xs font-medium"
                  >
                    <Layers className="h-3.5 w-3.5 mr-1" />
                    {(formDefaults as any).printLayoutId || formDefaults.layoutCode ? `Sửa bài bình ${formDefaults.layoutCode || ""}`.trim() : "Bình file"}
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentView("WORKBENCH")}
                  className="h-8 text-xs text-indigo-600 border-indigo-200 hover:bg-indigo-50 shadow-2xs font-medium"
                >
                  <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Đổi mặt hàng khác
                </Button>
              </div>
            </div>

            {/* Canonical Form đầy đủ 5 tabs */}
            <ProductionOrderForm
              key={formKey}
              orderId={undefined}
              hideStickyHeader={true}
              defaultValues={formDefaults}
              onSubmit={handleSubmit}
              onCancel={() => setCurrentView("WORKBENCH")}
              isSubmitting={isSubmitting}
              submitLabel="Lưu"
            />
          </div>
        )}

        {/* Modal bài bình Prepress legacy */}
        <SelectPrepressItemModal
          open={prepressModalOpen}
          onOpenChange={setPrepressModalOpen}
          onSelect={handleSelectLegacyPrepressBatch}
          currentSelectedCode={formDefaults.layoutCode}
          onManualEntry={() => {
            toast.info("Chế độ nhập thủ công: Người dùng tự điền các thông số.");
            setCurrentView("FORM");
          }}
        />
      </div>
    </StandardDetailLayout>
  );
}

const PO_CREATE_STEPS = [
  { key: "WORKBENCH", label: "Chọn mặt hàng" },
  { key: "PREPRESS", label: "Bình file" },
  { key: "FORM", label: "Lệnh sản xuất" },
] as const;

/** 3 bước tạo lệnh trên cùng một trang: chọn hàng → bình file (khi cần) → lệnh sản xuất */
function PoCreateStepper({ current }: { current: "WORKBENCH" | "PREPRESS" | "FORM" }) {
  const idx = PO_CREATE_STEPS.findIndex((st) => st.key === current);
  return (
    <ol className="flex items-center gap-1.5 sm:gap-2 text-[11px] sm:text-xs overflow-x-auto" data-testid="po-create-stepper">
      {PO_CREATE_STEPS.map((st, i) => (
        <li key={st.key} className="flex items-center gap-1.5 sm:gap-2 shrink-0" aria-current={i === idx ? "step" : undefined}>
          <span
            className={
              i === idx
                ? "inline-flex items-center gap-1 rounded-full bg-indigo-600 text-white px-2.5 py-1 font-semibold"
                : i < idx
                ? "inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-1"
                : "inline-flex items-center gap-1 rounded-full bg-slate-50 text-slate-500 border border-slate-200 px-2.5 py-1"
            }
          >
            {i < idx ? <CheckCircle2 className="h-3 w-3" /> : <span className="tabular-nums">{i + 1}.</span>}
            {st.label}
          </span>
          {i < PO_CREATE_STEPS.length - 1 && <span className="text-slate-300">›</span>}
        </li>
      ))}
    </ol>
  );
}
